/**
 * Slice 13 — Property Overview (ops home).
 * Needs attention · Upcoming turnovers · Supplies · Photos (manage).
 */
import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { usePropertyGalleryPhotos } from "@/hooks/usePropertyGalleryPhotos";
import { useCleaningTasks } from "@/hooks/useCleaningTasks";
import { useReservations } from "@/hooks/useReservations";
import { useInventoryItems } from "@/hooks/useInventory";
import { useIssuesInbox, issueNote } from "@/hooks/useIssuesInbox";
import { isIssueOpen } from "@/lib/issueTriage";
import { useUpdateProperty } from "@/hooks/useProperties";
import { useTurnoverTaskModal } from "@/contexts/TurnoverTaskModalContext";
import { persistPropertyGalleryPhotos } from "@/lib/propertyGalleryPhotos";
import {
  getInStockCount,
  getInventoryDisplayAndStatus,
} from "@/lib/inventoryQuantity";
import {
  type ReservationDateRow,
  type TurnoverChipState,
  chipClassName,
  resolveTurnoverWindow,
  todayYmd,
} from "@/lib/turnoverWindow";
import type { PropertyTabId } from "@/lib/propertyTabs";
import type { CleaningTaskWithRelations } from "@/types/cleaning";
import { formatPersonLabel, isTaskAssignedForSchedule } from "@/lib/taskAssigneeLabel";
import { OwnerGroupSentences } from "./OwnerGroupSentences";
import { OverviewSetupChecklist } from "./OverviewSetupChecklist";
import { isUntitledPropertyName } from "@/lib/unitSetupChecklist";
import { useSupplyForecast } from "@/hooks/useSupplyForecast";
import { formatBannerLine } from "@/lib/supplyForecast";

interface OverviewTabProps {
  propertyId: string;
  property: {
    name: string;
    address?: string | null;
    unit?: string | null;
    city?: string | null;
    state_province?: string | null;
    postal_code?: string | null;
    property_photo_url?: string | null;
    property_photo_urls?: string[] | null;
    hospitable_property_id?: string | null;
    owner_id?: string | null;
    entry_method?: string | null;
    entry_code?: string | null;
    entry_location?: string | null;
    access_notes?: string | null;
    amazon_buy_list_url?: string | null;
    setup_checklist_state?: unknown;
  };
  onTabChange?: (tab: PropertyTabId | string) => void;
}

function propertyTitle(name: string, unit?: string | null): string {
  const n = (name || "Property").trim();
  const u = (unit || "").trim();
  return u ? `${n} · ${u}` : n;
}

function addressLine(property: OverviewTabProps["property"]): string {
  const parts = [
    property.address,
    [property.city, property.state_province].filter(Boolean).join(" "),
    property.postal_code,
  ]
    .map((p) => (p || "").trim())
    .filter(Boolean);
  return parts.join(", ") || "No address";
}

function initialOf(name: string): string {
  const t = name.trim();
  return t ? t[0]!.toUpperCase() : "?";
}

function isAssigned(task: CleaningTaskWithRelations): boolean {
  return isTaskAssignedForSchedule(task, undefined);
}

type AttentionItem = {
  id: string;
  chip: string;
  chipClass: string;
  label: string;
  action: string;
  onAction: () => void;
};

type TurnoverRow = {
  task: CleaningTaskWithRelations;
  chip: TurnoverChipState;
  window: string;
  outSentence: string | null;
  inSentence: string | null;
  assigned: boolean;
  cleaner: string;
};

const OverviewTab = ({ propertyId, property, onTabChange }: OverviewTabProps) => {
  const queryClient = useQueryClient();
  const updateProperty = useUpdateProperty();
  const { openTurnoverTaskModal } = useTurnoverTaskModal();
  const fileRef = useRef<HTMLInputElement>(null);

  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reviewSetup, setReviewSetup] = useState(false);

  const gallery = usePropertyGalleryPhotos(propertyId, property);
  const { data: tasks = [] } = useCleaningTasks(propertyId);
  const { data: reservations = [] } = useReservations(propertyId);
  const { data: inventory = [] } = useInventoryItems(propertyId);
  const { data: issues = [] } = useIssuesInbox([propertyId]);
  const { attention: forecastAttention } = useSupplyForecast(propertyId);

  const cleanerIds = useMemo(
    () => [...new Set(tasks.map((t) => t.cleaner_id).filter(Boolean))] as string[],
    [tasks],
  );

  const { data: cleanerNames = {} } = useQuery({
    queryKey: ["overview-cleaner-names", cleanerIds.join(",")],
    queryFn: async () => {
      if (!cleanerIds.length) return {} as Record<string, string>;
      const { data, error } = await supabase.from("cleaners").select("id, name").in("id", cleanerIds);
      if (error) throw error;
      const map: Record<string, string> = {};
      for (const c of data || []) map[c.id] = formatPersonLabel(c.name) || c.name || "Cleaner";
      return map;
    },
    enabled: cleanerIds.length > 0,
    staleTime: 60_000,
  });

  const resRows = reservations as ReservationDateRow[];

  const turnoverRows: TurnoverRow[] = useMemo(() => {
    const today = todayYmd();
    return tasks
      .filter((t) => ["scheduled", "in_progress"].includes(t.status) && t.scheduled_date >= today)
      .map((task) => {
        const checkout =
          (task as { reservation?: { end_date?: string } | null }).reservation?.end_date ||
          task.scheduled_date;
        const resolved = resolveTurnoverWindow({
          checkoutDate: checkout,
          reservations: resRows,
          departingReservationId: task.reservation_id,
          taskStatus: task.status,
        });
        const assigned = isAssigned(task);
        const cleaner = assigned
          ? (task.cleaner_id && cleanerNames[task.cleaner_id]) || "Assigned"
          : "";
        return {
          task,
          chip: resolved.chip,
          window: resolved.label,
          outSentence: resolved.outSentence,
          inSentence: resolved.inSentence,
          assigned,
          cleaner,
        };
      })
      .sort((a, b) => a.task.scheduled_date.localeCompare(b.task.scheduled_date))
      .slice(0, 5);
  }, [tasks, resRows, cleanerNames]);

  const supplyRows = useMemo(() => {
    if (forecastAttention.length > 0) {
      return forecastAttention.slice(0, 2).map((r) => ({
        id: r.item_id,
        name: r.item_name,
        state: (r.status === "out" ? "Out" : r.status === "critical" ? "Critical" : "Order soon") as
          | "Low"
          | "Out"
          | "Critical"
          | "Order soon",
        detail: formatBannerLine(r),
        fromForecast: true as const,
      }));
    }
    return inventory
      .map((item) => {
        const status = getInventoryDisplayAndStatus(item);
        const stock = getInStockCount(item);
        const isOut =
          (item.quantity_type || "").toLowerCase() === "fullness"
            ? Number(item.current_value ?? 1) === 0
            : stock === 0;
        const state = isOut ? "Out" : status.isLow ? "Low" : null;
        if (!state) return null;
        return {
          id: item.id as string,
          name: String(
            (item as { item_name?: string; name?: string }).item_name ||
              (item as { name?: string }).name ||
              "Item",
          ),
          state: state as "Low" | "Out",
          detail: null as string | null,
          fromForecast: false as const,
        };
      })
      .filter(Boolean) as Array<{
      id: string;
      name: string;
      state: "Low" | "Out" | "Critical" | "Order soon";
      detail: string | null;
      fromForecast: boolean;
    }>;
  }, [inventory, forecastAttention]);

  const openIssues = useMemo(
    () => issues.filter((r) => isIssueOpen(r.status)).slice(0, 5),
    [issues],
  );

  const attention: AttentionItem[] = useMemo(() => {
    const items: AttentionItem[] = [];
    for (const row of turnoverRows) {
      if (!row.assigned) {
        items.push({
          id: `ua-${row.task.id}`,
          chip: "Unassigned",
          chipClass: "bg-[#fff4e5] text-[#b45309]",
          label: row.window,
          action: "Assign",
          onAction: () =>
            openTurnoverTaskModal({ mode: "edit", propertyId, taskId: row.task.id }),
        });
      } else if (row.chip === "Overdue" || row.chip === "Same day") {
        items.push({
          id: `to-${row.task.id}`,
          chip: row.chip,
          chipClass: chipClassName(row.chip),
          label: row.window + (row.cleaner ? ` · ${row.cleaner}` : ""),
          action: "Open",
          onAction: () => onTabChange?.("schedule"),
        });
      }
    }
    for (const s of supplyRows.slice(0, 4)) {
      items.push({
        id: `inv-${s.id}`,
        chip: s.state,
        chipClass: s.state === "Out" ? "bg-[#fbeae8] text-[#b4453a]" : "bg-[#fff4e5] text-[#b45309]",
        label: s.name,
        action: "Inventory",
        onAction: () => onTabChange?.("inventory"),
      });
    }
    for (const issue of openIssues.slice(0, 3)) {
      items.push({
        id: `iss-${issue.id}`,
        chip: "Issue",
        chipClass: "bg-[#fbeae8] text-[#b4453a]",
        label: issueNote(issue),
        action: "Open",
        onAction: () => onTabChange?.("maintenance"),
      });
    }
    return items.slice(0, 8);
  }, [turnoverRows, supplyRows, openIssues, openTurnoverTaskModal, propertyId, onTabChange]);

  const statusLine = useMemo(() => {
    const first = turnoverRows[0];
    if (!first) return { text: "No cleans soon", tone: "muted" as const };
    if (first.chip === "Overdue") {
      return { text: `${first.window} · overdue`, tone: "danger" as const };
    }
    return { text: first.window, tone: "ok" as const };
  }, [turnoverRows]);

  const nextTurnoverHeadline = useMemo(() => {
    const t = turnoverRows[0]?.task;
    if (!t) return "";
    const d = new Date(`${t.scheduled_date.slice(0, 10)}T12:00:00`);
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  }, [turnoverRows]);

  const untitled = isUntitledPropertyName(property.name);
  const title = untitled
    ? "Untitled property"
    : propertyTitle(property.name, property.unit);
  const address = untitled
    ? property.hospitable_property_id
      ? "Calendar synced from Hospitable · add a name in Settings"
      : addressLine(property)
    : addressLine(property);
  const fromListing = Boolean(property.hospitable_property_id);

  const persistGallery = async (urls: string[]) => {
    await persistPropertyGalleryPhotos(propertyId, urls);
    await updateProperty.mutateAsync({
      propertyId,
      property_photo_url: urls[0] ?? null,
      property_photo_urls: urls,
    });
    queryClient.invalidateQueries({ queryKey: ["property", propertyId] });
    queryClient.invalidateQueries({ queryKey: ["property-with-access", propertyId] });
  };

  const onAddPhotos = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const next = [...gallery];
      for (let i = 0; i < files.length; i++) {
        const file = files[i]!;
        const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
        const path = `${propertyId}/photo_${Date.now()}_${i}.${ext}`;
        const { error } = await supabase.storage.from("property-photos").upload(path, file, {
          upsert: true,
          contentType: file.type || "image/jpeg",
        });
        if (error) throw error;
        const { data } = supabase.storage.from("property-photos").getPublicUrl(path);
        if (data?.publicUrl) next.push(`${data.publicUrl}?t=${Date.now()}`);
      }
      await persistGallery(next);
      toast({ title: "Photos added" });
    } catch (e) {
      toast({
        title: "Couldn’t add photos",
        description: e instanceof Error ? e.message : undefined,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onRemoveSelected = async () => {
    const keep = gallery.filter((u) => !selected.has(u));
    try {
      await persistGallery(keep);
      setSelected(new Set());
      setSelectMode(false);
      setConfirmRemove(false);
      toast({ title: "Photos removed" });
    } catch (e) {
      toast({
        title: "Couldn’t remove photos",
        description: e instanceof Error ? e.message : undefined,
        variant: "destructive",
      });
    }
  };

  const toggleSelect = (url: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  };

  const sectionLabel = "text-[19px] font-medium tracking-[-0.2px] text-[#0f1720]";

  return (
    <div className="flex flex-col gap-[30px] px-1 pb-10 max-sm:gap-[22px]" data-tour="tour-overview-ops">
      {/* OV1 Subtitle (page title lives in the shell header) */}
      {untitled || statusLine ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[14px] font-medium text-[#64748b]">
          <span className="min-w-0 truncate">{address}</span>
          {!untitled ? (
            <span className="flex items-center gap-2">
              <span
                className={cn(
                  "h-[7px] w-[7px] shrink-0 rounded-full",
                  statusLine.tone === "danger" ? "bg-[#b4453a]" : statusLine.tone === "ok" ? "bg-[#24a89a]" : "bg-[#cdd8e0]",
                )}
              />
              <span
                className={cn(
                  "font-bold",
                  statusLine.tone === "danger" ? "text-[#b4453a]" : statusLine.tone === "ok" ? "text-[#15776d]" : "text-[#94a3b8]",
                )}
              >
                {statusLine.text}
              </span>
            </span>
          ) : null}
          {untitled ? (
            <button
              type="button"
              onClick={() => onTabChange?.("settings")}
              className="h-9 shrink-0 rounded-full border-[1.5px] border-[#cdd8df] bg-white px-[15px] text-[13px] font-bold text-[#475569] hover:border-[#24a89a] hover:text-[#15776d]"
            >
              Name this property
            </button>
          ) : null}
        </div>
      ) : null}

      {/* Slice 41 — Setup checklist */}
      <OverviewSetupChecklist
        propertyId={propertyId}
        property={property}
        onTabChange={onTabChange}
        forceExpanded={reviewSetup}
        onForceExpandedChange={setReviewSetup}
      />

      {/* OV3 Upcoming turnovers + Next turnover */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.65fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-2.5">
          <div className="flex items-baseline justify-between gap-3">
            <div className={sectionLabel}>Upcoming turnovers</div>
            <button
              type="button"
              onClick={() => onTabChange?.("schedule")}
              className="text-[13px] font-bold text-[#0f1720] hover:text-[#15776d]"
            >
              See schedule
            </button>
          </div>
          {turnoverRows.length === 0 ? (
            <div className="rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white px-5 py-6 text-[15px] font-medium text-[#94a3b8]">
              No turnovers yet
            </div>
          ) : (
            <div className="overflow-hidden rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white">
              {turnoverRows.map((r, i) => (
                <div
                  key={r.task.id}
                  className={cn(
                    "flex items-start gap-3 px-[18px] py-[14px]",
                    i < turnoverRows.length - 1 && "border-b-[1.5px] border-[#e6edf1]",
                    !r.assigned && "bg-[#fffdf8]",
                  )}
                >
                  <div className="flex w-[104px] shrink-0">
                    <span
                      className={cn(
                        "rounded-full px-3 py-1.5 text-[12px] font-bold tracking-[0.3px]",
                        chipClassName(r.chip),
                      )}
                    >
                      {r.chip}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[16px] font-bold tracking-[-0.2px] text-[#0f1720]">
                      {r.window}
                    </div>
                    <OwnerGroupSentences
                      outSentence={r.outSentence}
                      inSentence={r.inSentence}
                      className="mt-0.5"
                    />
                  </div>
                  {r.assigned ? (
                    <div className="flex shrink-0 items-center gap-2">
                      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#e2eeed] text-[12px] font-bold text-[#15776d]">
                        {initialOf(r.cleaner)}
                      </div>
                      <span className="hidden text-[15px] font-medium text-[#0f1720] sm:inline">
                        {r.cleaner}
                      </span>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() =>
                        openTurnoverTaskModal({
                          mode: "edit",
                          propertyId,
                          taskId: r.task.id,
                        })
                      }
                      className="h-9 shrink-0 rounded-[11px] bg-[#24a89a] px-4 text-[14px] font-bold text-white hover:bg-[#1f958a]"
                    >
                      Assign
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3.5 max-lg:order-first">
          <div className={sectionLabel}>Next turnover</div>
          {turnoverRows[0] ? (
            <div className="flex flex-1 flex-col gap-[18px] rounded-[24px] bg-[#e2eeed] p-6 max-sm:p-5">
              <div className="flex flex-col gap-1">
                <div className="text-[40px] font-medium leading-none tracking-[-1.2px] text-[#15776d] max-sm:text-[34px]">
                  {nextTurnoverHeadline}
                </div>
                <div className="text-[14px] font-medium text-[#3f6f69]">{turnoverRows[0].window}</div>
                <OwnerGroupSentences
                  outSentence={turnoverRows[0].outSentence}
                  inSentence={turnoverRows[0].inSentence}
                  className="mt-1"
                />
              </div>
              <div className="mt-auto flex items-center gap-[11px] rounded-2xl bg-white p-[11px]">
                <div
                  className={cn(
                    "flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[14px] font-bold",
                    turnoverRows[0].assigned ? "bg-[#24a89a] text-white" : "bg-[#f1f4f6] text-[#a3b1bc]",
                  )}
                >
                  {turnoverRows[0].assigned ? initialOf(turnoverRows[0].cleaner) : "?"}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[15px] font-bold text-[#0f1720]">
                    {turnoverRows[0].assigned ? turnoverRows[0].cleaner : "Unassigned"}
                  </div>
                </div>
                {!turnoverRows[0].assigned ? (
                  <button
                    type="button"
                    onClick={() =>
                      openTurnoverTaskModal({ mode: "edit", propertyId, taskId: turnoverRows[0]!.task.id })
                    }
                    className="h-9 shrink-0 rounded-[11px] bg-[#24a89a] px-4 text-[14px] font-bold text-white hover:bg-[#1f958a]"
                  >
                    Assign
                  </button>
                ) : (
                  <span className="text-[13px] font-medium text-[#64748b]">Assigned</span>
                )}
              </div>
            </div>
          ) : (
            <div className="rounded-[24px] bg-[#e2eeed] p-6 text-[15px] font-medium text-[#3f6f69]">No turnovers yet</div>
          )}
        </div>
      </div>

      {/* OV2 + OV4 Needs attention + Supplies */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="flex flex-col gap-3.5">
        <div className={sectionLabel}>Needs attention</div>
        {attention.length === 0 ? (
          <div className="flex items-center gap-2.5 py-0.5 text-[16px] font-medium text-[#94a3b8]">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#b5c2cc" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 13l4.5 4.5L19 7" />
            </svg>
            Nothing needs attention
          </div>
        ) : (
          <div className="overflow-hidden rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white">
            {attention.map((a, i) => (
              <div
                key={a.id}
                className={cn(
                  "flex items-center gap-3 px-[18px] py-[14px]",
                  i < attention.length - 1 && "border-b-[1.5px] border-[#e6edf1]",
                )}
              >
                <div className="flex w-[104px] shrink-0">
                  <span className={cn("rounded-full px-3 py-1.5 text-[12px] font-bold tracking-[0.3px]", a.chipClass)}>
                    {a.chip}
                  </span>
                </div>
                <div className="min-w-0 flex-1 truncate text-[16px] font-bold tracking-[-0.2px] text-[#0f1720]">
                  {a.label}
                </div>
                <button
                  type="button"
                  onClick={a.onAction}
                  className="h-9 shrink-0 rounded-[11px] bg-[#24a89a] px-4 text-[14px] font-bold text-white hover:bg-[#1f958a]"
                >
                  {a.action}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

        <div className="flex flex-col gap-2.5">
          <div className="flex items-baseline justify-between gap-3">
            <div className={sectionLabel}>Supplies</div>
            <button
              type="button"
              onClick={() => onTabChange?.("inventory")}
              className="text-[13px] font-bold text-[#15776d] hover:underline"
            >
              {supplyRows.some((s) => s.fromForecast) ? "See forecast →" : "Inventory"}
            </button>
          </div>
          {supplyRows.length === 0 ? (
            <div className="rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white px-5 py-6 text-[15px] font-medium text-[#94a3b8]">
              Supplies look good
            </div>
          ) : (
            <div className="overflow-hidden rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white">
              {supplyRows.some((s) => s.fromForecast) ? (
                <div className="border-b-[1.5px] border-[#e6edf1] bg-[#fff8e8] px-[18px] py-2.5 text-[12px] font-bold uppercase tracking-[0.4px] text-[#7a4505]">
                  Supplies to order
                </div>
              ) : null}
              {supplyRows.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => onTabChange?.("inventory")}
                  className={cn(
                    "flex w-full items-center gap-3 px-[18px] py-[14px] text-left hover:bg-[#f8fbfc]",
                    i < supplyRows.length - 1 && "border-b-[1.5px] border-[#e6edf1]",
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[16px] font-medium text-[#0f1720]">{s.name}</div>
                    {s.detail ? (
                      <div className="mt-0.5 truncate text-[13px] font-medium text-[#64748b]">
                        {s.detail.replace(/^Order\s+/i, "").replace(s.name, "").replace(/^soon\s*[—-]\s*/i, "").trim() ||
                          s.detail}
                      </div>
                    ) : null}
                  </div>
                  <span
                    className={cn(
                      "rounded-full px-3 py-1.5 text-[12px] font-bold",
                      s.state === "Out" || s.state === "Critical"
                        ? "bg-[#fbeae8] text-[#b4453a]"
                        : "bg-[#fff4e5] text-[#b45309]",
                    )}
                  >
                    {s.state}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* OV5 Photos */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-baseline gap-3">
            <div className={sectionLabel}>Photos</div>
            {gallery.length > 0 ? (
              <div className="text-[13px] font-medium text-[#64748b]">
                {gallery.length} photos{fromListing ? " · From listing" : ""}
              </div>
            ) : null}
          </div>
          <div className="flex items-center gap-2.5">
            {gallery.length > 0 ? (
              <button
                type="button"
                onClick={() => {
                  setSelectMode((v) => !v);
                  setSelected(new Set());
                }}
                className="flex h-9 items-center rounded-xl border-[1.5px] border-[#d7e0e6] bg-white px-3.5 text-[14px] font-bold text-[#334155]"
              >
                {selectMode ? "Done" : "Select"}
              </button>
            ) : null}
            {selectMode && selected.size > 0 ? (
              <button
                type="button"
                onClick={() => setConfirmRemove(true)}
                className="flex h-9 items-center rounded-xl bg-[#b4453a] px-3.5 text-[14px] font-bold text-white"
              >
                Remove {selected.size}
              </button>
            ) : (
              <button
                type="button"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
                className="flex h-9 items-center rounded-xl bg-[#24a89a] px-3.5 text-[14px] font-bold text-white hover:bg-[#1f958a] disabled:opacity-60"
              >
                {uploading ? "Uploading…" : "Add"}
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => onAddPhotos(e.target.files)}
            />
          </div>
        </div>

        {gallery.length === 0 ? (
          <div className="flex h-[180px] flex-col items-center justify-center gap-2 rounded-[18px] border-2 border-dashed border-[#e6edf1] bg-[#f4f7f9]">
            <div className="text-lg font-bold text-[#475569]">No photos yet</div>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="text-[14px] font-bold text-[#15776d]"
            >
              Add photos
            </button>
          </div>
        ) : (
          <div className="rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white p-3">
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8">
              {gallery.map((url) => {
                const isSel = selected.has(url);
                return (
                  <button
                    key={url}
                    type="button"
                    onClick={() => {
                      if (selectMode) toggleSelect(url);
                      else setLightbox(url);
                    }}
                    className={cn(
                      "relative aspect-square overflow-hidden rounded-[10px] bg-[#eef3f6]",
                      selectMode && isSel && "ring-2 ring-[#24a89a] ring-offset-2",
                    )}
                  >
                    <img src={url} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
                    {selectMode ? (
                      <div
                        className={cn(
                          "absolute left-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-md border-2",
                          isSel
                            ? "border-[#24a89a] bg-[#24a89a] text-white"
                            : "border-white bg-black/20",
                        )}
                      >
                        {isSel ? "✓" : null}
                      </div>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {lightbox ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setLightbox(null)}
        >
          <img
            src={lightbox}
            alt=""
            className="max-h-[90vh] max-w-[90vw] rounded-xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      ) : null}

      {confirmRemove ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-lg">
            <div className="text-lg font-bold text-[#0f1720]">
              Remove {selected.size} photo{selected.size === 1 ? "" : "s"}?
            </div>
            <p className="mt-2 text-sm font-medium text-[#64748b]">This can’t be undone from Overview.</p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmRemove(false)}
                className="h-10 rounded-xl border-[1.5px] border-[#d7e0e6] px-4 text-sm font-bold text-[#334155]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onRemoveSelected}
                className="h-10 rounded-xl bg-[#b4453a] px-4 text-sm font-bold text-white"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default OverviewTab;
